import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useQuery } from "react-query";
import Accordion from "react-bootstrap/Accordion";
import { useTranslation } from "react-i18next";
import { IFaqItem, IFaqResponse } from "../../types/faq";
import { getInternalPortalFaqsAllPerPage } from "../../api/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import Pagination from "react-bootstrap/Pagination";
import { IInnerProject } from "../../types/inner_page";

interface ProjectFaqWidgetProps {
  language: string;
  pageItem: IInnerProject
}

const ITEMS_PER_PAGE = 500;

const ProjectFaqWidget: React.FC<ProjectFaqWidgetProps> = ({ pageItem }: { pageItem: IInnerProject }) => {
  const { i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1); // Track total pages

  const { isLoading, error, data, refetch } = useQuery<IFaqResponse>(
    ["faqQuestionsData", i18n.language, currentPage],
    () => getInternalPortalFaqsAllPerPage({ language: i18n.language, page: currentPage, pageSize: ITEMS_PER_PAGE }),
    {
      onSuccess: (responseData) => {
        setTotalPages(responseData.meta.pagination.pageCount);
      },
    }
  );

  useEffect(() => {
    refetch();
  }, [i18n.language, currentPage, refetch]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading data...</div>;
  if (!data) return <div>No data available</div>;

  return (
    <div>
      <Accordion defaultActiveKey="0" className="custom-accordion">
        {data.data.map((faqRow: IFaqItem, idx: number) => (
          <Accordion.Item eventKey={idx.toString()} key={faqRow.id} className="accordion-item">
            <Accordion.Header className="accordion-header">
              <FontAwesomeIcon icon={faQuestionCircle} className="faq-icon me-2 p-1" />
              {faqRow.attributes.Title}
            </Accordion.Header>
            <Accordion.Body className="accordion-content">
              <ReactMarkdown>{faqRow.attributes.Description}</ReactMarkdown>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      {/* Enhanced Pagination Controls */}
      {totalPages>1 && <Pagination className="mt-3 en_font">
        <Pagination.Prev
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        />
        {[...Array(totalPages)].map((_, pageIndex) => (
          <Pagination.Item
            key={pageIndex + 1}
            active={pageIndex + 1 === currentPage}
            onClick={() => setCurrentPage(pageIndex + 1)}
          >
            {pageIndex + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        />
      </Pagination>}
    </div>
  );
};

export default ProjectFaqWidget;
